import { createApp } from "vue";
import { createPinia } from "pinia";
import App from "./App.vue";
import { router } from "./router";
import { useAuthStore } from "./stores/auth";
import { useThemeStore } from "./stores/theme";
import { i18n } from "./i18n";
import { reportClientError } from "./lib/errorReporter";
import "./style.css";

const pinia = createPinia();

const app = createApp(App);
app.use(pinia);
app.use(router);
app.use(i18n);

// ── Global error capture (stability, 2026-06-14) ──────────────────────────
// Until now an uncaught error vanished into a white screen with no signal.
// These handlers keep console.error (so dev still sees the trace) AND ship a
// best-effort report to the backend. Wired BEFORE app.mount so the very first
// render is already covered. reportClientError is dedupe/rate-limited and
// never throws — see src/lib/errorReporter.ts.
app.config.errorHandler = (err, _instance, info) => {
  console.error(err);
  reportClientError({
    message: String((err as Error)?.message ?? err),
    stack: (err as Error)?.stack,
    info,
    source: "vue",
  });
};
window.addEventListener("error", (e) =>
  reportClientError({ message: e.message, stack: e.error?.stack, source: "window.onerror" }),
);
window.addEventListener("unhandledrejection", (e) =>
  reportClientError({
    message: String(e.reason?.message ?? e.reason),
    stack: e.reason?.stack,
    source: "unhandledrejection",
  }),
);

// Initialise theme (applies .dark to <html> + tracks OS preference changes).
const theme = useThemeStore();
theme.applyToDom();
theme.bindSystemListener();

// Bootstrap auth (try to fetch current user; sets `authed` if cookie valid).
const auth = useAuthStore();
// NEW-DEMO-2026-08-07-02: ждём И bootstrap, И готовности роутера перед mount. Иначе первый кадр
// рендерит AppShell (админ-меню/навигацию) ДО разрешения публичного bare-роута (/s /q /r) —
// на публичной ссылке опроса мелькала админка CDP. С router.isReady() route.meta.bare уже
// определён к первому рендеру, публичная страница сразу bare.
Promise.allSettled([auth.bootstrap(), router.isReady()]).finally(() => {
  app.mount("#app");
});
